import StackTrace from 'stacktrace-js'
import {
    BookingflowStateMachine,
    DKK,
    Service,
} from '@raskrask-dk/bookingflow-state-machine'
import {
    GetData,
    StateAny,
} from '@raskrask-dk/bookingflow-state-machine/lib/bookingflowstatemachine/bookingflowstatemachine.types'
import { ReviewBF, PartnerBF, GoalBF } from '../models'

/**
 * Creates and POSTs an error to our API
 * The API can do whatever it wants. At time of writing it's passed to PM2
 *
 * @param error - what is thrown inside React
 * @param BFSM - used to deduce important information
 */
const myErrorHandler = (error: Error, BFSM: BookingflowStateMachine): void => {
    //const host = process.env.REACT_APP_API_URL || 'localhost'
    const baseUrl = process.env.REACT_APP_API_URL || ''
    const url = `${baseUrl}/v1/bookingflow/error`

    StackTrace.fromError(error).then((err) => {
        const pm2Error = new PM2ErrorBuilder()
            .setName('BookingFlow Error')
            .setState(BFSM.currentState().type)
            .setMessage(error.message)
            .addBFChoices(BFSM.getData())
            .setOptionalExtra(
                `${err[0].fileName}\nLine: ${err[0].lineNumber} \n \n  ${error.stack}`
            )
            .getError()

        const header = {
            headers: {
                'Content-Type': 'application/json',
            },
            method: 'POST',
            body: pm2Error,
        }

        fetch(url, header)
    })
}

export const getErrorHandler = (): ((
    error: Error,
    BFSM: BookingflowStateMachine
) => void) => {
    const isProd = process.env.NODE_ENV === 'production'
    return isProd ? myErrorHandler : console.error
}

/**
 * Builds an error
 * Allows method chaining
 * Returns .getError() as json for fetch
 */
class PM2ErrorBuilder {
    private _name?: string
    private _state?: string
    private _message?: string
    private _optionalExtra?: string

    setName = (name: string) => {
        this._name = name
        return this
    }
    setState = (state: string) => {
        this._state = state
        return this
    }
    setMessage = (message: string) => {
        this._message = message
        return this
    }
    addBFChoices = (getData: GetData<StateAny>) => {
        this._message += '\n\n' + stringifyWithCircular(getData)
        return this
    }
    setOptionalExtra = (optionalExtra: string) => {
        this._optionalExtra = optionalExtra
        return this
    }

    public getError() {
        const json = JSON.stringify({
            name: this._name,
            state: this._state,
            message: this._message,
            optionalExtra: this._optionalExtra,
        })
        return json
    }
}
interface Offer {
    service: Service
    price: DKK
}
const stringifyWithCircular = (obj: GetData<StateAny>): string => {
    const cache: any[] = []
    const str = JSON.stringify(obj, function (key, value) {
        if (typeof value === 'object' && value !== null) {
            if (cache.indexOf(value) !== -1) {
                // Circular reference found, discard key
                return
            }
            const isReview = value instanceof ReviewBF
            if (Array.isArray(value)) {
                const isPartnerBFArray = value[0] instanceof PartnerBF
                if (isPartnerBFArray) {
                    const newPartnerBFArray = []
                    for (let i = 0; i < value.length; i++) {
                        const originalPartner = value[i] as PartnerBF
                        const offersArray = Array.from(
                            originalPartner.offers,
                            function (entry) {
                                const originalOffer = entry[1] as Offer
                                const modifiedOffer = {
                                    price: originalOffer.price,
                                    service: {
                                        duration:
                                            originalOffer.service.duration,
                                        id: originalOffer.service.id,
                                        name: originalOffer.service.name,
                                        freeConsultation:
                                            originalOffer.service
                                                .freeConsultation,
                                    },
                                }
                                return { key: entry[0], value: modifiedOffer }
                            }
                        )
                        const textsArray = Array.from(
                            originalPartner.texts,
                            function (entry) {
                                return { key: entry[0], value: entry[1] }
                            }
                        )
                        const periodArray = Array.from(
                            originalPartner.schedule.periods(),
                            function (entry) {
                                console.log(entry)
                                return {
                                    from: entry.from.toString(),
                                    to: entry.to.toString(),
                                }
                            }
                        )

                        const partnerWithoutReviews = {
                            id: originalPartner.id,
                            alias: originalPartner.alias,
                            education: originalPartner.education,
                            gdprlink: originalPartner.gdprLink,
                            texts: textsArray,
                            tpictureLink: originalPartner.therapistPictureLink,
                            yotpoRating: originalPartner.yotpoRating,
                            offers: offersArray,
                            schedule: periodArray,
                        }
                        newPartnerBFArray.push(partnerWithoutReviews)
                    }
                    cache.push(newPartnerBFArray)
                    return newPartnerBFArray
                }
            }
            if (value instanceof GoalBF) {
                const originalGoal = value as GoalBF

                const modifiedGoal = {
                    id: originalGoal.id,
                    name: originalGoal.name,
                    icon: originalGoal.icon,
                    text: originalGoal.text,
                }
                cache.push(modifiedGoal)
                return modifiedGoal
            }
            if (value instanceof PartnerBF) {
                const originalPartner = value as PartnerBF
                const modifiedPartner = {
                    id: originalPartner.id,
                    alias: originalPartner.alias,
                    education: originalPartner.education,
                    gdprlink: originalPartner.gdprLink,

                    tpictureLink: originalPartner.therapistPictureLink,
                    yotpoRating: originalPartner.yotpoRating,
                }
                cache.push(modifiedPartner)
                return modifiedPartner
            }
            if (!isReview) {
                // Store value in our collection
                cache.push(value)
            }
        }
        return value
    })
    return str
}
